// Copyright 2021 Pangea Cyber Corporation
// Author: Pangea Cyber Corporation

import { useEffect } from "react";
import isEmpty from "lodash/isEmpty";
import { UTM_COOKIE_NAME } from "@src/helpers/utms";

const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

/**
 * isLocalhost - helper function to determine if a hostname is localhost
 * @param hostname {string}
 * @returns {boolean}
 * @example
 * isLocalhost("pangea.cloud");
 * // false
 *
 * isLocalhost("127.0.0.1");
 * // true
 */
function isLocalhost(hostname: string): boolean {
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return true;
  }

  return false;
}

function maybeAddSecureFlag(cookie: string, domain: string): string {
  // Check the location.hostname
  // if we're not at localhost, then add the Secure flag
  // and also set the domain
  if (!isLocalhost(window.location.hostname)) {
    cookie += "; Secure";
    cookie += `; domain=${domain}`;
  }

  return cookie;
}

export function useUTMCodes() {
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      typeof document !== "undefined" &&
      process.env.GATSBY_FF_DOCS_UTM_CODE === "true" &&
      window?.location?.search !== ""
    ) {
      const searchParams = new URLSearchParams(window?.location?.search);
      const UTMCodesObj: Record<string, string> = {};

      searchParams.forEach((value, key) => {
        const normalizedKey = key.toLowerCase();

        /**
         * We want to track:
         * 1) "utm" -> any utm code
         * 2) "gad_source" -> comes from google ads
         * 3) "gclid" -> comes from google search/ds
         */
        if (
          normalizedKey.startsWith("utm") ||
          normalizedKey === "gad_source" ||
          normalizedKey === "gclid"
        ) {
          UTMCodesObj[normalizedKey] = value;
        }
      });

      if (!isEmpty(UTMCodesObj)) {
        try {
          const utmParams = new URLSearchParams(UTMCodesObj);
          let cookie = `${UTM_COOKIE_NAME}=${utmParams.toString()}; path=/;max-age=${ONE_YEAR_IN_SECONDS}`;
          cookie = maybeAddSecureFlag(cookie, "pangea.cloud");
          document.cookie = cookie;
        } catch (ignore) {
          console.error(ignore);
        }
      }
    }
  }, []);
}
